import { useEffect, useMemo, useState } from 'react'
import { Copy } from '@/lib/copy-context'
import { fetchCopyMap, upsertCopy } from '@/lib/copy'

interface CopyRow {
  key: string;
  value: string;
}

type StatusType = 'idle' | 'saving' | 'success' | 'error';

const statusMessage: Record<StatusType, string> = {
  idle: '',
  saving: 'Saving…',
  success: 'Saved!',
  error: 'Something went wrong. Please try again.',
}

export default function AdminCopy() {
  const [rows, setRows] = useState<CopyRow[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [status, setStatus] = useState<StatusType>('idle')

  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        const fresh = await fetchCopyMap()
        if (!active) return
        const nextRows = Object.entries(fresh).map(([key, value]) => ({ key, value }))
        setRows(nextRows.length ? nextRows : [{ key: '', value: '' }])
      } catch (error) {
        if (!active) return
        setRows([{ key: '', value: '' }])
        setStatus('error')
      }
    })()
    return () => {
      active = false
    }
  }, [])

  const addRow = () => {
    setRows((prev) => [...prev, { key: '', value: '' }])
  }

  const update = (index: number, field: keyof CopyRow, value: string) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)))
  }

  const save = async () => {
    setLoading(true)
    setStatus('saving')
    try {
      const payload = rows
        .filter((row) => row.key.trim().length)
        .map((row) => ({ key: row.key.trim(), value: row.value }))
      if (payload.length) {
        await upsertCopy(payload)
      }
      setStatus('success')
    } catch (error) {
      setStatus('error')
    } finally {
      setLoading(false)
    }
  }

  const ariaMessage = useMemo(() => statusMessage[status] ?? '', [status])

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-3xl font-semibold text-cocoa">
          <Copy k="admin.copy.title">Site Copy</Copy>
        </h1>
        <p className="text-cocoa/70">
          <Copy k="admin.copy.subtitle">Manage the public-facing copy used across Project Tower pages.</Copy>
        </p>
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="badge bg-brick text-white hover:bg-brickDark transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick"
          onClick={addRow}
          aria-label="Add new copy key"
        >
          Add key
        </button>
        <button
          type="button"
          className="badge bg-cocoa text-white hover:bg-cocoa/90 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick"
          onClick={save}
          disabled={loading}
          aria-label="Save copy entries"
        >
          {loading ? 'Saving…' : 'Save'}
        </button>
        <span className="text-sm text-cocoa/60" aria-live="polite" role="status">
          {ariaMessage}
        </span>
      </div>
      <div className="space-y-4">
        {rows.map((row, index) => (
          <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start rounded-2xl bg-shell p-4 shadow-soft">
            <label className="flex flex-col gap-2 md:col-span-1 text-sm font-medium text-cocoa/80">
              Key
              <input
                className="input"
                placeholder="key (e.g., home.hero.title)"
                value={row.key}
                aria-label="Copy key"
                onChange={(event) => update(index, 'key', event.target.value)}
              />
            </label>
            <label className="flex flex-col gap-2 md:col-span-2 text-sm font-medium text-cocoa/80">
              Value
              <textarea
                className="textarea"
                placeholder="value"
                rows={3}
                value={row.value}
                aria-label={`Copy value for ${row.key || 'new key'}`}
                onChange={(event) => update(index, 'value', event.target.value)}
              />
            </label>
          </div>
        ))}
      </div>
    </div>
  )
}
